import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import RcTooltip from 'rc-tooltip';

const ParetoChart = ({
  width = 800,
  height = 400,
  data = {},
  type = "Dataset",
  selectedMetric = null,
}) => {
  const svgRef = useRef();
  const scrollContainerRef = useRef();
  const panStateRef = useRef({ active: false, startX: 0, scrollLeft: 0 });
  const chartModelRef = useRef(null);
  const [panAvailability, setPanAvailability] = useState({ left: false, right: false });
  const isAttributeChart = type === "Attribute";
  const minimumChartHeight = isAttributeChart ? 380 : 300;
  const renderedHeight = Math.max(Number(height) || 0, minimumChartHeight);

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) return;

    let formattedData = [];

    //  Handle Dataset case
    if (type === "Dataset") {
      formattedData = Object.values(data)
        .filter(metric => metric && typeof metric.value === "number" && !isNaN(metric.value))
        .map(metric => ({
          category: metric.name,
          value: metric.value,
        }));
    } 
    //  Handle Attribute case
    else if (type === "Attribute") {
      let contributionData = [];

      // Case 1: array-of-arrays like [[375107,88],[375059,42]]
      if (Array.isArray(data) && Array.isArray(data[0])) {
        contributionData = data;
      } 

      formattedData = contributionData
        .filter(([name, val]) => typeof val === "number" && !isNaN(val))
        .map(([name, val]) => ({
          category: String(name),
          value: val,
        }));
    }

    if (formattedData.length === 0) return;

    //  D3 setup
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Add font-family to SVG
    svg.style("font-family", "Poppins");

    // Attribute values can be long and are rendered diagonally. Reserve enough
    // room inside the SVG so the browser does not clip their lower edge.
    const margin = { top: 30, right: 50, bottom: isAttributeChart ? 120 : 70, left: 50 };
    // Keep bars readable instead of squeezing all categories into the narrow
    // details panel. The containing element provides horizontal panning.
    const longestCategoryLength = d3.max(
      formattedData,
      item => String(item.category || '').length
    ) || 0;
    const minimumCategoryWidth = Math.min(
      64,
      Math.max(isAttributeChart ? 24 : 22, longestCategoryLength * 3.2)
    );
    const renderedWidth = Math.max(
      Number(width) || 0,
      formattedData.length * minimumCategoryWidth + margin.left + margin.right
    );
    const innerWidth = renderedWidth - margin.left - margin.right;
    const innerHeight = renderedHeight - margin.top - margin.bottom;

    const chart = svg
      .attr('width', renderedWidth)
      .attr('height', renderedHeight)
      .attr('viewBox', `0 0 ${renderedWidth} ${renderedHeight}`)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    //  Sort descending
    formattedData.sort((a, b) => b.value - a.value);

    //  Cumulative %
    const total = d3.sum(formattedData, d => d.value);
    let cumulative = 0;
    formattedData.forEach(d => {
      cumulative += d.value;
      d.cumulative = total > 0 ? (cumulative / total) * 100 : 0;
    });

    //  Scales
    const x = d3.scaleBand()
      .domain(formattedData.map(d => d.category))
      .range([0, innerWidth])
      .padding(0.12);
    const maximumBarWidth = formattedData.length <= 6 ? 40 : 24;
    const barWidth = Math.min(x.bandwidth(), maximumBarWidth);
    const barOffset = (x.bandwidth() - barWidth) / 2;

    const yLeft = d3.scaleLinear()
      .domain([0, d3.max(formattedData, d => d.value)])
      .nice()
      .range([innerHeight, 0]);

    const yRight = d3.scaleLinear()
      .domain([0, 100])
      .range([innerHeight, 0]);

    // Add horizontal grid lines for bar chart values
    const yTicks = yLeft.ticks(5);
    chart.selectAll(".horizontal-grid")
      .data(yTicks)
      .join("line")
      .attr("class", "horizontal-grid")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", d => yLeft(d))
      .attr("y2", d => yLeft(d))
      .attr("stroke", "var(--realtime-chart-separator-bg)")
      .attr("stroke-width", 1)
      .lower(); // Places grid lines behind other elements

    //  Left Y-axis
    chart.append('g')
      .attr('class', 'pareto-y-axis pareto-y-axis--left')
      .call(d3.axisLeft(yLeft))
      .selectAll("text")
      .style("font-size", "12px")
      .style("color", "var(--realtime-chart-stat-caption-color)");

    //  Right Y-axis
    chart.append('g')
      .attr('class', 'pareto-y-axis pareto-y-axis--right')
      .attr('transform', `translate(${innerWidth},0)`)
      .call(d3.axisRight(yRight).ticks(10).tickFormat(d => d + "%"))
      .selectAll("text")
      .style("font-size", "12px")
      .style("color", "var(--realtime-chart-stat-caption-color)");

    // Opaque gutters keep bars, grid lines, and cumulative labels from showing
    // through the sticky axis text while the plot is panned underneath it.
    chart.select('.pareto-y-axis--left')
      .insert('rect', ':first-child')
      .attr('class', 'pareto-axis-gutter')
      .attr('x', -margin.left)
      .attr('y', 0)
      .attr('width', margin.left + 8)
      .attr('height', innerHeight)
      .attr('fill', 'var(--realtime-stat-layout-bg, #fff)');
    chart.select('.pareto-y-axis--right')
      .insert('rect', ':first-child')
      .attr('class', 'pareto-axis-gutter')
      .attr('x', -8)
      .attr('y', 0)
      .attr('width', margin.right + 8)
      .attr('height', innerHeight)
      .attr('fill', 'var(--realtime-stat-layout-bg, #fff)');

    //  X-axis
    chart.append('g')
      .attr('class', 'pareto-x-axis')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", longestCategoryLength > 4 ? "rotate(-48)" : null)
      .style("text-anchor", longestCategoryLength > 4 ? "end" : "middle")
      .attr("dx", longestCategoryLength > 4 ? "-0.45em" : "0")
      .attr("dy", longestCategoryLength > 4 ? "0.35em" : "0.75em")
      .style("font-size", "9px")
      .style("color", "var(--realtime-chart-stat-caption-color)");

    //  Style axis lines - remove left and right domain lines, keep tick lines
    chart.selectAll(".domain").remove(); // This removes all domain lines (left, right, bottom)
    
    chart.selectAll(".tick line")
      .style("stroke", "var(--realtime-chart-separator-bg)");

    //  Tooltip
    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "pareto-chart-tooltip")
      .style("position", "absolute")
      .style("z-index", 9999)
      .style("background", "rgba(24, 24, 38, 0.96)")
      .style("color", "#fff")
      .style("padding", "8px 10px")
      .style("border", "1px solid rgba(255, 255, 255, 0.16)")
      .style("border-radius", "6px")
      .style("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.2)")
      .style("font-size", "11px")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("font-family", "Poppins");

    const showTooltip = (item) => {
      const pointerEvent = d3.event;
      tooltip.html("");
      tooltip.append("div")
        .style("font-weight", 600)
        .style("margin-bottom", "3px")
        .text(item.category);
      tooltip.append("div").text(`Value: ${item.value}`);
      tooltip.append("div").text(`Cumulative: ${item.cumulative.toFixed(1)}%`);
      tooltip
        .style("left", `${pointerEvent.pageX + 12}px`)
        .style("top", `${pointerEvent.pageY - 18}px`)
        .style("opacity", 1);
    };

    const moveTooltip = () => {
      const pointerEvent = d3.event;
      tooltip
        .style("left", `${pointerEvent.pageX + 12}px`)
        .style("top", `${pointerEvent.pageY - 18}px`);
    };

    const hideTooltip = () => tooltip.style("opacity", 0);

    //  Bars with new color and rounded tops
    chart.selectAll(".bar")
      .data(formattedData)
      .join("rect")
      .attr("class", "bar")
      .attr("fill", "rgba(137, 101, 240, 0.75)")
      .attr("x", d => x(d.category) + barOffset)
      .attr("y", d => yLeft(d.value))
      .attr("width", barWidth)
      .attr("height", d => innerHeight - yLeft(d.value))
      .attr("rx", 4) // Horizontal radius for rounded corners
      .attr("ry", 4)
      .style("cursor", "pointer")
      .on("mouseenter", showTooltip)
      .on("mousemove", moveTooltip)
      .on("mouseleave", hideTooltip); // Vertical radius for rounded corners

    //  Line with new color
    const line = d3.line()
      .x(d => x(d.category) + x.bandwidth() / 2)
      .y(d => yRight(d.cumulative))
      .curve(d3.curveMonotoneX);

    chart.append("path")
      .datum(formattedData)
      .attr("fill", "none")
      .attr("stroke", "rgba(255, 99, 132, 1)")
      .attr("stroke-width", 2)
      .attr("d", line);

    // A marker for every cumulative value makes each line position explicit
    // and gives mouse/touch users a reliable tooltip target.
    chart.selectAll(".pareto-line-point")
      .data(formattedData)
      .join("circle")
      .attr("class", "pareto-line-point")
      .attr("cx", d => x(d.category) + x.bandwidth() / 2)
      .attr("cy", d => yRight(d.cumulative))
      .attr("r", 3.5)
      .attr("fill", "#fff")
      .attr("stroke", "rgba(255, 99, 132, 1)")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .on("mouseenter", function (item) {
        d3.select(this).attr("r", 5);
        showTooltip(item);
      })
      .on("mousemove", moveTooltip)
      .on("mouseleave", function () {
        d3.select(this).attr("r", 3.5);
        hideTooltip();
      });

    chartModelRef.current = {
      chart,
      formattedData,
      x,
      yLeft,
      innerHeight,
      innerWidth,
      margin,
    };

    // // Alternative: Remove only left and right domain lines, keep bottom
    // chart.select(".domain").remove(); // Removes the first domain line found
    // chart.selectAll("g").selectAll(".domain")
    //   .filter(function() {
    //     return d3.select(this.parentNode).attr("transform") !== `translate(0,${innerHeight})`;
    //   })
    //   .remove();

    requestAnimationFrame(() => syncPanPosition());

    return () => {
      chartModelRef.current = null;
      tooltip.remove();
    };
  }, [data, type, selectedMetric, width, renderedHeight, isAttributeChart]);

  const syncPanPosition = () => {
    const container = scrollContainerRef.current;
    const svgNode = svgRef.current;
    if (!container || !svgNode) return;

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const scrollLeft = Math.max(0, Math.min(container.scrollLeft, maxScrollLeft));
    const marginLeft = 50;
    const marginRight = 50;
    const chartInnerWidth = Math.max(0, Number(svgNode.getAttribute('width')) - marginLeft - marginRight);
    const visibleInnerWidth = Math.max(0, container.clientWidth - marginLeft - marginRight);
    const stickyRightX = Math.min(chartInnerWidth, scrollLeft + visibleInnerWidth);
    const chartModel = chartModelRef.current;

    if (chartModel) {
      const {
        chart,
        formattedData,
        x,
        yLeft,
        innerHeight,
        innerWidth,
      } = chartModel;
      const visibleStart = Math.max(0, scrollLeft - marginLeft);
      const visibleEnd = scrollLeft + container.clientWidth - marginLeft;
      const visibleData = formattedData.filter((item) => {
        const itemX = x(item.category);
        return itemX !== undefined
          && itemX + x.bandwidth() >= visibleStart
          && itemX <= visibleEnd;
      });
      const visibleMaximum = d3.max(visibleData, item => item.value)
        || d3.max(formattedData, item => item.value)
        || 1;

      yLeft.domain([0, visibleMaximum]).nice();

      chart.select('.pareto-y-axis--left')
        .call(d3.axisLeft(yLeft).ticks(5))
        .selectAll('text')
        .style('font-size', '12px')
        .style('color', 'var(--realtime-chart-stat-caption-color)');

      chart.selectAll('.bar')
        .attr('y', item => yLeft(item.value))
        .attr('height', item => innerHeight - yLeft(item.value));

      const visibleTicks = yLeft.ticks(5);
      chart.selectAll('.horizontal-grid')
        .data(visibleTicks)
        .join('line')
        .attr('class', 'horizontal-grid')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', tick => yLeft(tick))
        .attr('y2', tick => yLeft(tick))
        .attr('stroke', 'var(--realtime-chart-separator-bg)')
        .attr('stroke-width', 1)
        .lower();

      chart.selectAll('.domain').remove();
      chart.selectAll('.tick line')
        .style('stroke', 'var(--realtime-chart-separator-bg)');

      // Only show labels whose bars are inside the visible plot area. Without
      // this, labels belonging to off-screen bars leak past the sticky Y-axis.
      chart.select('.pareto-x-axis')
        .selectAll('.tick')
        .style('visibility', category => {
          const categoryX = x(category);
          if (categoryX === undefined) return 'hidden';
          const viewportX = marginLeft + categoryX + (x.bandwidth() / 2) - scrollLeft;
          return viewportX >= marginLeft + 4
            && viewportX <= container.clientWidth - marginRight - 4
            ? 'visible'
            : 'hidden';
        });
    }

    d3.select(svgNode)
      .select('.pareto-y-axis--left')
      .attr('transform', `translate(${scrollLeft},0)`)
      .raise();
    d3.select(svgNode)
      .select('.pareto-y-axis--right')
      .attr('transform', `translate(${stickyRightX},0)`)
      .raise();

    setPanAvailability({
      left: scrollLeft > 1,
      right: scrollLeft < maxScrollLeft - 1,
    });
  };

  const handlePanStart = (event) => {
    const container = scrollContainerRef.current;
    if (!container || container.scrollWidth <= container.clientWidth) return;

    panStateRef.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: container.scrollLeft,
    };
    container.setPointerCapture(event.pointerId);
    container.style.cursor = 'grabbing';
  };

  const handlePanMove = (event) => {
    const container = scrollContainerRef.current;
    const panState = panStateRef.current;
    if (!container || !panState.active) return;

    container.scrollLeft = panState.scrollLeft - (event.clientX - panState.startX);
    syncPanPosition();
  };

  const handlePanEnd = (event) => {
    const container = scrollContainerRef.current;
    if (!container || !panStateRef.current.active) return;

    panStateRef.current.active = false;
    if (container.hasPointerCapture(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
    container.style.cursor = 'grab';
  };

  const handleHorizontalWheel = (event) => {
    const container = scrollContainerRef.current;
    if (!container || container.scrollWidth <= container.clientWidth) return;

    // Preserve normal vertical panel scrolling. Horizontal trackpad gestures
    // and Shift + mouse wheel pan the chart left/right.
    const horizontalDelta = event.deltaX || (event.shiftKey ? event.deltaY : 0);
    if (!horizontalDelta) return;
    event.preventDefault();
    container.scrollLeft += horizontalDelta;
    syncPanPosition();
  };

  const panChart = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({
      left: direction * Math.max(120, container.clientWidth * 0.65),
      behavior: 'smooth',
    });
  };

  return (
    <div
      className="pareto-chart-shell"
      style={{
        position: 'relative',
        minHeight: `${renderedHeight}px`,
        height: `${renderedHeight}px`,
        width: '100%',
        maxWidth: '100%',
        flexShrink: 0,
      }}
    >
      <div
        ref={scrollContainerRef}
        className={`pareto-chart-container${isAttributeChart ? ' pareto-chart-container--attribute' : ''}`}
        style={{
          height: '100%',
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          cursor: 'grab',
          touchAction: 'pan-y',
          userSelect: 'none',
        }}
        onPointerDown={handlePanStart}
        onPointerMove={handlePanMove}
        onPointerUp={handlePanEnd}
        onPointerCancel={handlePanEnd}
        onWheel={handleHorizontalWheel}
        onScroll={syncPanPosition}
      >
        <svg
          ref={svgRef}
          className="pareto-chart"
          style={{ display: 'block', minHeight: `${renderedHeight}px`, maxWidth: 'none' }}
        />
      </div>
      {panAvailability.left && (
        <RcTooltip
          placement="top"
          overlay={<span>Previous</span>}
          arrowContent={<div className="rc-tooltip-arrow-inner" />}
        >
          <button
            type="button"
            className="pareto-chart-pan-indicator pareto-chart-pan-indicator--left"
            aria-label="Previous chart values"
            onClick={() => panChart(-1)}
            style={panIndicatorStyle('left')}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M8.75 3.5L5.25 7L8.75 10.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </RcTooltip>
      )}
      {panAvailability.right && (
        <RcTooltip
          placement="top"
          overlay={<span>Next</span>}
          arrowContent={<div className="rc-tooltip-arrow-inner" />}
        >
          <button
            type="button"
            className="pareto-chart-pan-indicator pareto-chart-pan-indicator--right"
            aria-label="Next chart values"
            onClick={() => panChart(1)}
            style={panIndicatorStyle('right')}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </RcTooltip>
      )}
    </div>
  );
};

const panIndicatorStyle = (side) => ({
  position: 'absolute',
  top: '50%',
  [side]: '8px',
  transform: 'translateY(-50%)',
  zIndex: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  padding: 0,
  border: '1px solid rgba(104, 71, 217, 0.28)',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.96)',
  color: '#6847D9',
  boxShadow: '0 2px 6px rgba(31, 22, 66, 0.16)',
  cursor: 'pointer',
  outline: 'none',
});

export default ParetoChart;

// import React, { useEffect, useRef } from 'react';
// import * as d3 from 'd3';

// const ParetoChart = ({ 
//                         width = 800, 
//                         height = 400,
//                         data=[
//                                 { category: 'A', value: 40 },
//                                 { category: 'B', value: 25 },
//                                 { category: 'C', value: 20 },
//                                 { category: 'D', value: 10 },
//                                 { category: 'E', value: 5 },
//                         ],
//                         type="Dataset"
// }) => {
//   const svgRef = useRef();


//   useEffect(() => {

//     //console.log(JSON.stringify(data))

//     // const convertedData = {};

//     // top_20_frequency.forEach((item, index) => {
//     //   const date = item[0];
//     //   const value = item[1];
//     //   convertedData[index] = { name: date, value: value };
//     // });

//     console.log("**************")
//     console.log(JSON.stringify(data))
//     console.log("**************")
//     let formattedData = []

//     if(type === "Attribute" && Array.isArray(data)){
//       formattedData = data.map((data)=> {
//                         return { category: type,cumulative : 0, name: data[0], value: data[1]}
//                     })
//     }
//     else{
//           formattedData = Object.values(data).map(item => ({
//             category: item.category,
//             value: item.value,
//             cumulative : 0,
//           }));
//     }

//     const svg = d3.select(svgRef.current);
//     svg.selectAll('*').remove();

//     const margin = { top: 30, right: 50, bottom: 70, left: 50 };
//     const innerWidth = width - margin.left - margin.right;
//     const innerHeight = height - margin.top - margin.bottom;

//     const chart = svg
//       .attr('width', width)
//       .attr('height', height)
//       .append('g')
//       .attr('transform', `translate(${margin.left},${margin.top})`);

//     // Sort by descending value
//     formattedData.sort((a, b) => b.value - a.value);

//     // Cumulative percentage
//     const total = d3.sum(formattedData, d => d.value);
//     let cumulative = 0;
//     formattedData.forEach(d => {
//       cumulative += d.value;
//       d.cumulative = (cumulative / total) * 100;
//     });

//     // Scales
//     const x = d3.scaleBand()
//       .domain(formattedData.map(d => d.category))
//       .range([0, innerWidth])
//       .padding(0.1);

//     const yLeft = d3.scaleLinear()
//       .domain([0, d3.max(formattedData, d => d.value)])
//       .nice()
//       .range([innerHeight, 0]);

//     const yRight = d3.scaleLinear()
//       .domain([0, 100])
//       .range([innerHeight, 0]);

//     // Axes
//     chart.append('g').call(d3.axisLeft(yLeft));

//     chart.append('g')
//       .attr('transform', `translate(${width},0)`)
//       .call(d3.axisRight(yRight).ticks(10).tickFormat(d => d + "%"));

//     chart.append('g')
//       .attr('transform', `translate(0,${height})`)
//       .call(d3.axisBottom(x))
//       .selectAll("text")
//       .attr("transform", "rotate(-30)")
//       .style("text-anchor", "end");

//     // Tooltip
//     const tooltip = d3.select("body")
//       .append("div")
//       .style("position", "absolute")
//       .style("background", "#fff")
//       .style("padding", "5px 10px")
//       .style("border", "1px solid #ccc")
//       .style("border-radius", "5px")
//       .style("pointer-events", "none")
//       .style("opacity", 0);

//     // Bars
//     chart.selectAll(".bar")
//       .data(formattedData)
//       .join("rect")
//       .attr("class", "bar")
//       .attr("fill", "steelblue")
//       .attr("x", d => x(d.category) + (x.bandwidth() - 10) / 2) // Center the 10px bar
//       .attr("y", d => yLeft(d.value))
//       .attr("width", 10) // Fixed bar width 10px
//       .attr("height", d => innerHeight - yLeft(d.value))
//       .on("mouseover", (event, d) => {
//         tooltip.transition().duration(200).style("opacity", 1);
//         tooltip.html(`Value: ${d.value}<br/>Cumulative: ${d.cumulative!==undefined && d.cumulative.toFixed(1)}%`)
//           .style("left", `${event.pageX + 10}px`)
//           .style("top", `${event.pageY - 28}px`);
//       })
//       .on("mouseout", () => {
//         tooltip.transition().duration(200).style("opacity", 0);
//       });

//     // Line
//     const line = d3.line()
//       .x(d => x(d.category) + x.bandwidth() / 2)
//       .y(d => yRight(d.cumulative));

//     chart.append("path")
//       .datum(formattedData)
//       .attr("class", "line")
//       .attr("fill", "none")
//       .attr("stroke", "orange")
//       .attr("stroke-width", 2)
//       .attr("d", line);

//     // Cumulative labels
//     chart.selectAll(".label")
//       .data(formattedData)
//       .join("text")
//       .attr("x", d => x(d.category) + x.bandwidth() / 2)
//       .attr("y", d => yRight(d.cumulative) - 5)
//       .attr("text-anchor", "middle")
//       .attr("font-size", "10px")
//       .text(d => d.cumulative.toFixed(0) + "%");

//     // Clean up tooltip on unmount
//     return () => {
//       tooltip.remove();
//     };

//   }, 
//   // [width, height,data]
// );

//   return (
//     <div>
//       <svg ref={svgRef}></svg>
//     </div>
//   );
// };

// export default ParetoChart;



// import React, { useEffect, useRef } from 'react';
// import * as d3 from 'd3';

// const ParetoChart = () => {
//   const svgRef = useRef();

//   useEffect(() => {
//     const data = [
//       { category: 'A', value: 40 },
//       { category: 'B', value: 25 },
//       { category: 'C', value: 20 },
//       { category: 'D', value: 10 },
//       { category: 'E', value: 5 }
//     ];

//     const svg = d3.select(svgRef.current);
//     svg.selectAll('*').remove();

//     const margin = { top: 30, right: 50, bottom: 70, left: 50 };
//     const width = 800 - margin.left - margin.right;
//     const height = 400 - margin.top - margin.bottom;

//     const chart = svg
//       .attr('width', width + margin.left + margin.right)
//       .attr('height', height + margin.top + margin.bottom)
//       .append('g')
//       .attr('transform', `translate(${margin.left},${margin.top})`);

//     // Sort by descending value
//     data.sort((a, b) => b.value - a.value);

//     // Cumulative percentage
//     const total = d3.sum(data, d => d.value);
//     let cumulative = 0;
//     data.forEach(d => {
//       cumulative += d.value;
//       d.cumulative = (cumulative / total) * 100;
//     });

//     // Scales
//     const x = d3.scaleBand()
//       .domain(data.map(d => d.category))
//       .range([0, width])
//       .padding(0.1);

//     const yLeft = d3.scaleLinear()
//       .domain([0, d3.max(data, d => d.value)])
//       .nice()
//       .range([height, 0]);

//     const yRight = d3.scaleLinear()
//       .domain([0, 100])
//       .range([height, 0]);

//     // Axes
//     chart.append('g').call(d3.axisLeft(yLeft));

//     chart.append('g')
//       .attr('transform', `translate(${width},0)`)
//       .call(d3.axisRight(yRight).ticks(10).tickFormat(d => d + "%"));

//     chart.append('g')
//       .attr('transform', `translate(0,${height})`)
//       .call(d3.axisBottom(x))
//       .selectAll("text")
//       .attr("transform", "rotate(-30)")
//       .style("text-anchor", "end");

//     // Tooltip
//     const tooltip = d3.select("body")
//       .append("div")
//       .style("position", "absolute")
//       .style("background", "#fff")
//       .style("padding", "5px 10px")
//       .style("border", "1px solid #ccc")
//       .style("border-radius", "5px")
//       .style("pointer-events", "none")
//       .style("opacity", 0);

//     // Bars
//     chart.selectAll(".bar")
//       .data(data)
//       .join("rect")
//       .attr("class", "bar")
//       .attr("fill", "steelblue")
//       .attr("x", d => x(d.category))
//       .attr("y", d => yLeft(d.value))
//       .attr("width", x.bandwidth())
//       .attr("height", d => height - yLeft(d.value))
//       .on("mouseover", (event, d) => {
//         tooltip.transition().duration(200).style("opacity", 1);
//         tooltip.html(`Value: ${d.value}<br/>Cumulative: ${d.cumulative.toFixed(1)}%`)
//           .style("left", `${event.pageX + 10}px`)
//           .style("top", `${event.pageY - 28}px`);
//       })
//       .on("mouseout", () => {
//         tooltip.transition().duration(200).style("opacity", 0);
//       });

//     // Line
//     const line = d3.line()
//       .x(d => x(d.category) + x.bandwidth() / 2)
//       .y(d => yRight(d.cumulative));

//     chart.append("path")
//       .datum(data)
//       .attr("class", "line")
//       .attr("fill", "none")
//       .attr("stroke", "orange")
//       .attr("stroke-width", 2)
//       .attr("d", line);

//     // Cumulative labels
//     chart.selectAll(".label")
//       .data(data)
//       .join("text")
//       .attr("x", d => x(d.category) + x.bandwidth() / 2)
//       .attr("y", d => yRight(d.cumulative) - 5)
//       .attr("text-anchor", "middle")
//       .attr("font-size", "10px")
//       .text(d => d.cumulative.toFixed(0) + "%");
//   }, []);

//   return (
//     <div>
//       <svg ref={svgRef}></svg>
//     </div>
//   );
// };

// export default ParetoChart;



// import ParetoChart from './ParetoChart'; // Adjust the path if needed

// function App() {
//   return (
//     <div className="App">
//       <h2>Pareto Chart</h2>
//       <ParetoChart />
//     </div>
//   );
// }

// export default App;
